/**
 * ExportService
 * 
 * Handles data export in JSON/CSV formats, premium image report cards,
 * and professional printable report generation.
 */
export class ExportService {
  /**
   * Export data as a file
   * @param {Object|Array} data - Data to export
   * @param {string} fileName - Name of the file
   * @param {string} type - 'json' | 'csv'
   */
  exportFile(data, fileName, type = 'json') {
    let content = '';
    let mimeType = '';

    if (type === 'json') {
      content = JSON.stringify(data, null, 2);
      mimeType = 'application/json';
    } else if (type === 'csv') {
      content = this.convertToCSV(data);
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.${type}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Convert array of objects to CSV string
   * @param {Array<Object>} data 
   * @returns {string}
   */
  convertToCSV(data) {
    if (!Array.isArray(data) || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const rows = data.map(obj =>
      headers.map(header => {
        let val = obj[header];
        if (typeof val === 'string') val = `"${val.replace(/"/g, '""')}"`;
        return val;
      }).join(',')
    );

    return [headers.join(','), ...rows].join('\n');
  }

  /**
   * Export a multi-section analytical CSV report from the enriched payload.
   * @param {Object} payload - The full buildExportPayload() output
   * @param {string} fileName - Base filename
   */
  exportCSVReport(payload, fileName) {
    const lines = [];
    const sep = '========================================';

    // Header
    lines.push(`${sep}`);
    lines.push(`CYTIFIC KB TESTER — SESSION REPORT`);
    lines.push(`${sep}`);
    lines.push(`Generated:,${payload.timestamp}`);
    lines.push(`Session Duration:,${payload.sessionDuration}`);
    lines.push(`URL:,${payload.url || 'N/A'}`);
    lines.push(`Environment:,"${(payload.environment || '').replace(/"/g, '""')}"`);
    lines.push('');

    // Configuration
    lines.push(`=== CONFIGURATION ===`);
    lines.push(`Setting,Value`);
    const cfg = payload.configuration || {};
    lines.push(`Layout,${cfg.layout || 'N/A'}`);
    lines.push(`Style,${cfg.style || 'N/A'}`);
    lines.push(`Standard,${cfg.standard || 'N/A'}`);
    lines.push(`OS Mode,${cfg.os || 'N/A'}`);
    lines.push(`Detected Layout,${cfg.detectedLayout || 'NOT_TESTED'}`);
    lines.push('');

    // Performance Metrics
    lines.push(`=== PERFORMANCE METRICS ===`);
    lines.push(`Metric,Value,Unit`);
    const perf = payload.performance || {};
    lines.push(`Polling Rate,${perf.pollingHz || '---'},Hz`);
    lines.push(`Peak Latency,${perf.latencyPeakMs || '---'},ms`);
    lines.push(`Max Simultaneous Keys,${perf.maxSimultaneous || 0},keys`);
    lines.push(`APM (Actions/Min),${perf.apm || 0},actions/min`);
    lines.push(`CPS (Current),${perf.cps || 0},keys/sec`);
    lines.push(`CPS (Peak),${perf.maxCps || 0},keys/sec`);
    lines.push('');

    // Heatmap — Top Keys
    const heatmap = payload.heatmap || {};
    lines.push(`=== KEY USAGE HEATMAP ===`);
    lines.push(`Total Key Presses:,${heatmap.totalPresses || 0}`);
    lines.push(`Unique Keys Used:,${heatmap.uniqueKeysUsed || 0}`);
    lines.push('');
    lines.push(`Rank,Key,Press Count`);
    (heatmap.topKeys || []).forEach(k => {
      lines.push(`${k.rank},${k.key},${k.count}`);
    });
    lines.push('');

    // Diagnostics
    lines.push(`=== DIAGNOSTICS ===`);
    lines.push(`Diagnostic,Count,Status`);
    const diag = payload.diagnostics || {};
    const chatterStatus = (diag.chatterCount || 0) === 0 ? 'EXCELLENT' : (diag.chatterCount < 5 ? 'WARNING' : 'CRITICAL');
    const stuckStatus = (diag.stuckCount || 0) === 0 ? 'EXCELLENT' : 'WARNING';
    lines.push(`Chatter Events,${diag.chatterCount || 0},${chatterStatus}`);
    lines.push(`Stuck Key Alerts,${diag.stuckCount || 0},${stuckStatus}`);
    lines.push(`Chord Split Faults,${diag.chordSplitCount || 0},${(diag.chordSplitCount || 0) === 0 ? 'EXCELLENT' : 'INFO'}`);
    if (diag.repeatRate) {
      lines.push(`Repeat Rate,${diag.repeatRate},ms`);
    }
    lines.push('');

    // NKRO Results
    const nkro = payload.nkroResults || [];
    if (nkro.length > 0) {
      lines.push(`=== NKRO TEST RESULTS ===`);
      lines.push(`Zone,Expected,Actual,Status`);
      nkro.forEach(r => {
        lines.push(`${(r.zone || '').toUpperCase()},${r.expected},${r.actual},${r.pass ? 'PASS' : 'FAIL'}`);
      });
      lines.push('');
    }

    // Raw Event Log
    const events = payload.eventLog || [];
    if (events.length > 0) {
      lines.push(`=== RAW EVENT LOG (${events.length} events) ===`);
      if (typeof events[0] === 'object') {
        const headers = Object.keys(events[0]);
        lines.push(headers.join(','));
        events.forEach(evt => {
          lines.push(headers.map(h => {
            let v = evt[h];
            if (typeof v === 'string') v = `"${v.replace(/"/g, '""')}"`;
            return v;
          }).join(','));
        });
      } else {
        events.forEach(evt => lines.push(`"${String(evt).replace(/"/g, '""')}"`));
      }
    }

    lines.push('');
    lines.push(`${sep}`);
    lines.push(`Report generated by Cytific KB Tester`);
    lines.push(`No keystroke data was stored on external servers.`);
    lines.push(`${sep}`);

    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/csv; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Trigger browser print dialog with a comprehensive professional report
   * @param {Object} reportData - Full report payload including topKeys, apm, cps, diagnostics
   */
  printReport(reportData) {
    const printWindow = window.open('', '_blank');
    const {
      appName, timestamp, hz, maxSimultaneous, chatterCount,
      results, apm, cps, maxCps, stuckCount, chordSplitCount,
      totalPresses, topKeys, latencyPeakMs, sessionDuration,
      layout, standard, environment
    } = reportData;

    // Generate verdict
    const issues = (chatterCount || 0) + (stuckCount || 0) + (chordSplitCount || 0);
    let verdict = 'EXCELLENT';
    let verdictColor = '#00ff9d';
    let verdictText = 'No issues detected. Your keyboard is performing flawlessly.';
    if (issues > 0 && issues <= 3) {
      verdict = 'GOOD';
      verdictColor = '#f0c040';
      verdictText = 'Minor issues detected. Your keyboard is mostly reliable.';
    } else if (issues > 3) {
      verdict = 'NEEDS ATTENTION';
      verdictColor = '#ff4444';
      verdictText = 'Multiple issues detected. Consider cleaning or replacing switches.';
    }

    // Top keys bar chart
    const maxCount = topKeys && topKeys.length > 0 ? topKeys[0].count : 1;
    const topKeysHTML = (topKeys || []).map(k => {
      const pct = Math.round((k.count / maxCount) * 100);
      return `
        <div style="display:flex;align-items:center;gap:10px;margin:4px 0;">
          <span style="width:80px;font-weight:700;text-align:right;font-family:monospace;">${k.key}</span>
          <div style="flex:1;background:#eee;border-radius:4px;height:20px;overflow:hidden;">
            <div style="width:${pct}%;height:100%;background:linear-gradient(90deg,#00cc7a,#00ff9d);border-radius:4px;"></div>
          </div>
          <span style="width:50px;text-align:right;font-family:monospace;font-size:12px;">${k.count}</span>
        </div>`;
    }).join('');

    // NKRO results table
    const nkroHTML = results.length > 0 ? `
      <h2 style="margin-top:30px;border-bottom:1px solid #ddd;padding-bottom:5px;">NKRO Test Results</h2>
      <table>
        <thead><tr><th>Zone</th><th>Expected</th><th>Actual</th><th>Status</th></tr></thead>
        <tbody>
          ${results.map(r => `
            <tr>
              <td>${(r.zone || '').toUpperCase()}</td>
              <td>${r.expected}</td>
              <td>${r.actual}</td>
              <td style="color:${r.pass ? '#00aa55' : '#cc0000'};font-weight:700;">${r.pass ? '✓ PASS' : '✗ FAIL'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>` : '';

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${appName} - Test Report</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; padding: 40px; color: #111; background: #fff; max-width: 800px; margin: 0 auto; }
          h1 { font-size: 28px; margin-bottom: 4px; }
          .subtitle { color: #888; font-size: 12px; margin-bottom: 20px; }
          .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 24px; font-size: 13px; color: #555; }
          .verdict-box { background: #f8f9fa; border-left: 4px solid ${verdictColor}; padding: 16px 20px; margin-bottom: 24px; border-radius: 4px; }
          .verdict-label { font-size: 20px; font-weight: 800; color: ${verdictColor}; margin-bottom: 4px; }
          .verdict-text { font-size: 13px; color: #555; }
          .metrics-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
          .metric-box { background: #f8f9fa; padding: 14px; border-radius: 6px; text-align: center; }
          .metric-value { font-size: 28px; font-weight: 800; color: #111; font-family: monospace; }
          .metric-label { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #888; margin-top: 4px; }
          h2 { font-size: 16px; color: #333; margin-top: 24px; }
          .diag-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; }
          .diag-item { text-align: center; padding: 10px; background: #f8f9fa; border-radius: 6px; }
          .diag-count { font-size: 24px; font-weight: 800; font-family: monospace; }
          .diag-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #888; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
          th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
          th { background: #f8f9fa; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
          .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #eee; font-size: 11px; color: #aaa; text-align: center; }
          @media print { .no-print { display: none; } body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${appName.toUpperCase()}</h1>
        <div class="subtitle">Keyboard Performance Report</div>

        <div class="meta-grid">
          <div><strong>Date:</strong> ${timestamp}</div>
          <div><strong>Session:</strong> ${sessionDuration || 'N/A'}</div>
          <div><strong>Layout:</strong> ${(layout || 'N/A').toUpperCase()} / ${(standard || 'N/A').toUpperCase()}</div>
          <div><strong>Total Presses:</strong> ${totalPresses || 0}</div>
        </div>

        <div class="verdict-box">
          <div class="verdict-label">${verdict}</div>
          <div class="verdict-text">${verdictText}</div>
        </div>

        <div class="metrics-grid">
          <div class="metric-box">
            <div class="metric-value">${apm || 0}</div>
            <div class="metric-label">APM</div>
          </div>
          <div class="metric-box">
            <div class="metric-value">${maxCps || 0}</div>
            <div class="metric-label">Peak CPS</div>
          </div>
          <div class="metric-box">
            <div class="metric-value">${maxSimultaneous || 0}</div>
            <div class="metric-label">Max NKRO</div>
          </div>
          <div class="metric-box">
            <div class="metric-value">${hz || '---'}</div>
            <div class="metric-label">Polling (Hz)</div>
          </div>
          <div class="metric-box">
            <div class="metric-value">${latencyPeakMs || '---'}</div>
            <div class="metric-label">Latency (ms)</div>
          </div>
          <div class="metric-box">
            <div class="metric-value">${totalPresses || 0}</div>
            <div class="metric-label">Total Presses</div>
          </div>
        </div>

        <h2 style="border-bottom:1px solid #ddd;padding-bottom:5px;">Diagnostics</h2>
        <div class="diag-grid">
          <div class="diag-item">
            <div class="diag-count" style="color:${(chatterCount || 0) === 0 ? '#00aa55' : '#cc0000'}">${chatterCount || 0}</div>
            <div class="diag-label">Chatter</div>
          </div>
          <div class="diag-item">
            <div class="diag-count" style="color:${(stuckCount || 0) === 0 ? '#00aa55' : '#cc0000'}">${stuckCount || 0}</div>
            <div class="diag-label">Stuck Keys</div>
          </div>
          <div class="diag-item">
            <div class="diag-count" style="color:${(chordSplitCount || 0) === 0 ? '#00aa55' : '#f0a020'}">${chordSplitCount || 0}</div>
            <div class="diag-label">Chord Split</div>
          </div>
        </div>

        ${topKeys && topKeys.length > 0 ? `
        <h2 style="border-bottom:1px solid #ddd;padding-bottom:5px;">Most Used Keys</h2>
        ${topKeysHTML}` : ''}

        ${nkroHTML}

        <div class="footer">
          &copy; <span class="current-year">2026</span>. CYTIFIC LABS, ALL RIGHTS RESERVED.<br>
          No keystroke data was stored on external servers.<br>
          <strong>tools.cytificlabs.com/kb-tester</strong>
        </div>

        <script>
          window.onload = () => { window.print(); };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  }

  /**
   * Export a premium Canvas-drawn report card as a PNG image (1200×675, 16:9).
   * Uses Canvas 2D API directly — no html2canvas dependency.
   * @param {Object} data - The full buildReportPayload() output
   * @param {string} fileName - Base filename
   */
  exportImageReport(data, fileName) {
    const SCALE = 3; // 3x High-DPI Resolution
    const W = 1200;
    const H = 700;
    const canvas = document.createElement('canvas');
    canvas.width = W * SCALE;
    canvas.height = H * SCALE;
    const ctx = canvas.getContext('2d');

    // Scale all drawing operations to the high DPI resolution natively
    ctx.scale(SCALE, SCALE);

    // Colors
    const BG_TOP = '#0e1117';
    const BG_BOT = '#080a10';
    const GREEN = '#00ff9d';
    const DIM = 'rgba(255,255,255,0.35)';
    const WHITE = '#ffffff';
    const CARD_BG = 'rgba(255,255,255,0.04)';
    const BORDER = 'rgba(255,255,255,0.08)';

    // Background gradient
    const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
    bgGrad.addColorStop(0, BG_TOP);
    bgGrad.addColorStop(1, BG_BOT);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Subtle grid pattern
    ctx.strokeStyle = 'rgba(255,255,255,0.02)';
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    // Header bar
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(0, 0, W, 56);
    ctx.fillStyle = GREEN;
    ctx.font = 'bold 16px "Segoe UI", system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText('CYTIFIC KB TESTER', 28, 28);
    ctx.fillStyle = DIM;
    ctx.font = '12px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`Report — ${data.timestamp || new Date().toLocaleString()}`, W - 28, 28);
    ctx.textAlign = 'left';

    // Accent line under header
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, 56, W, 2);

    // === VERDICT ===
    const issues = (data.chatterCount || 0) + (data.stuckCount || 0) + (data.chordSplitCount || 0);
    let verdictLabel = 'EXCELLENT';
    let verdictColor = GREEN;
    if (issues > 0 && issues <= 3) { verdictLabel = 'GOOD'; verdictColor = '#f0c040'; }
    else if (issues > 3) { verdictLabel = 'NEEDS ATTENTION'; verdictColor = '#ff4444'; }

    ctx.fillStyle = verdictColor;
    ctx.font = 'bold 22px "Segoe UI", system-ui, sans-serif';
    ctx.fillText(`VERDICT: ${verdictLabel}`, 28, 90);

    ctx.fillStyle = DIM;
    ctx.font = '11px "Segoe UI", system-ui, sans-serif';
    ctx.fillText(`Session: ${data.sessionDuration || 'N/A'}  |  Layout: ${(data.layout || 'N/A').toUpperCase()}  |  Total Presses: ${data.totalPresses || 0}`, 28, 112);

    // Helper: draw a metric card
    const drawCard = (x, y, w, h, value, label, unit) => {
      ctx.fillStyle = CARD_BG;
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 1;
      this._roundRect(ctx, x, y, w, h, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = WHITE;
      ctx.font = 'bold 32px "Segoe UI", system-ui, monospace';
      ctx.textAlign = 'center';
      ctx.fillText(String(value), x + w / 2, y + h / 2 - 4);

      ctx.fillStyle = DIM;
      ctx.font = '10px "Segoe UI", system-ui, sans-serif';
      ctx.fillText(label.toUpperCase(), x + w / 2, y + h - 14);
      if (unit) {
        ctx.fillStyle = GREEN;
        ctx.font = '10px monospace';
        ctx.fillText(unit, x + w / 2, y + 16);
      }
      ctx.textAlign = 'left';
    };

    // Performance Cards Row (6 cards)
    const cardW = 175;
    const cardH = 80;
    const cardY = 134;
    const cardGap = 12;
    const startX = 28;

    drawCard(startX, cardY, cardW, cardH, data.apm || 0, 'APM', '');
    drawCard(startX + (cardW + cardGap), cardY, cardW, cardH, data.maxCps || 0, 'Peak CPS', '');
    drawCard(startX + 2 * (cardW + cardGap), cardY, cardW, cardH, data.maxSimultaneous || 0, 'Max NKRO', 'KEYS');
    drawCard(startX + 3 * (cardW + cardGap), cardY, cardW, cardH, data.hz || '---', 'Polling', 'HZ');
    drawCard(startX + 4 * (cardW + cardGap), cardY, cardW, cardH, data.latencyPeakMs || '---', 'Latency', 'MS');
    drawCard(startX + 5 * (cardW + cardGap), cardY, cardW, cardH, data.totalPresses || 0, 'Total Presses', '');

    // Divider
    const divY = cardY + cardH + 20;
    ctx.fillStyle = GREEN;
    ctx.font = 'bold 12px "Segoe UI", system-ui, sans-serif';
    ctx.fillText('TOP KEYS', 28, divY + 4);

    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fillRect(28, divY + 14, W - 56, 1);

    const divY2 = divY;
    ctx.fillStyle = GREEN;
    ctx.textAlign = 'right';
    ctx.fillText('DIAGNOSTICS', W - 28, divY2 + 4);
    ctx.textAlign = 'left';

    // Top Keys (left column — 5 keys with bars)
    const topKeys = data.topKeys || [];
    const barStartY = divY + 28;
    const barMaxW = 450;
    const maxCount = topKeys.length > 0 ? topKeys[0].count : 1;

    topKeys.forEach((k, i) => {
      const y = barStartY + i * 36;
      const pct = k.count / maxCount;
      const barW = Math.max(pct * barMaxW, 4);

      // Rank
      ctx.fillStyle = DIM;
      ctx.font = 'bold 12px monospace';
      ctx.fillText(`${k.rank}.`, 28, y + 10);

      // Key name
      ctx.fillStyle = WHITE;
      ctx.font = 'bold 12px "Segoe UI", system-ui, sans-serif';
      ctx.fillText(k.key, 56, y + 10);

      // Bar
      const barGrad = ctx.createLinearGradient(150, 0, 150 + barW, 0);
      barGrad.addColorStop(0, 'rgba(0,255,157,0.6)');
      barGrad.addColorStop(1, 'rgba(0,255,157,0.15)');
      ctx.fillStyle = barGrad;
      this._roundRect(ctx, 150, y, barW, 16, 3);
      ctx.fill();

      // Count
      ctx.fillStyle = GREEN;
      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(String(k.count), 150 + barMaxW + 40, y + 10);
      ctx.textAlign = 'left';
    });

    // Diagnostics (right column)
    const diagX = W - 320;
    const diagItems = [
      { label: 'CHATTER', value: data.chatterCount || 0, good: (data.chatterCount || 0) === 0 },
      { label: 'STUCK KEYS', value: data.stuckCount || 0, good: (data.stuckCount || 0) === 0 },
      { label: 'CHORD SPLIT', value: data.chordSplitCount || 0, good: (data.chordSplitCount || 0) === 0 }
    ];

    diagItems.forEach((d, i) => {
      const y = barStartY + i * 48;

      ctx.fillStyle = CARD_BG;
      ctx.strokeStyle = BORDER;
      this._roundRect(ctx, diagX, y - 4, 290, 40, 6);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = d.good ? GREEN : '#ff4444';
      ctx.font = 'bold 22px monospace';
      ctx.fillText(String(d.value), diagX + 16, y + 18);

      ctx.fillStyle = DIM;
      ctx.font = '11px "Segoe UI", system-ui, sans-serif';
      ctx.fillText(d.label, diagX + 60, y + 18);

      ctx.fillStyle = d.good ? GREEN : '#ff4444';
      ctx.textAlign = 'right';
      ctx.font = 'bold 11px "Segoe UI", system-ui, sans-serif';
      ctx.fillText(d.good ? '✓ OK' : '⚠ ALERT', diagX + 274, y + 18);
      ctx.textAlign = 'left';
    });

    // NKRO results compact line
    const nkro = data.results || [];
    if (nkro.length > 0) {
      const nkroY = barStartY + diagItems.length * 48 + 10;
      ctx.fillStyle = DIM;
      ctx.font = '10px "Segoe UI", system-ui, sans-serif';
      const passCount = nkro.filter(r => r.pass).length;
      ctx.fillText(`NKRO: ${passCount}/${nkro.length} zones passed`, diagX, nkroY + 8);
    }

    // Footer bar
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.fillRect(0, H - 44, W, 44);
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, H - 44, W, 1);

    ctx.fillStyle = DIM;
    ctx.font = '10px "Segoe UI", system-ui, sans-serif';
    ctx.fillText('tools.cytificlabs.com/kb-tester', 28, H - 20);

    ctx.textAlign = 'right';
    ctx.fillText('No data sent to external servers.', W - 28, H - 20);
    ctx.textAlign = 'left';

    // Download
    const imageURL = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = imageURL;
    link.download = `${fileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Draw a rounded rectangle path.
   * @private
   */
  _roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  /**
   * Generate a compact, branded "Share Card" PNG for social sharing.
   * Uses Canvas 2D API directly — produces a 600×340 card.
   * Falls back to download if navigator.share is unavailable.
   * @param {Object} data - The buildReportPayload() output
   */
  async exportShareCard(data) {
    const SCALE = 3; // 3x High-DPI Resolution for pristine social sharing
    const W = 600;
    const H = 520;
    const canvas = document.createElement('canvas');
    canvas.width = W * SCALE;
    canvas.height = H * SCALE;
    const ctx = canvas.getContext('2d');

    // Natively scale all drawing instructions for maximum crispness
    ctx.scale(SCALE, SCALE);

    const GREEN = '#00ff9d';
    const DIM = 'rgba(255,255,255,0.35)';

    // Background
    const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
    bgGrad.addColorStop(0, '#0e1117');
    bgGrad.addColorStop(1, '#080a10');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Subtle grid
    ctx.strokeStyle = 'rgba(255,255,255,0.02)';
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 30) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += 30) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    // Header
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(0, 0, W, 44);
    ctx.fillStyle = GREEN;
    ctx.font = 'bold 14px "Segoe UI", system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText('CYTIFIC KB TESTER', 20, 22);
    ctx.fillStyle = DIM;
    ctx.font = '10px "Segoe UI", sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(data.timestamp || new Date().toLocaleString(), W - 20, 22);
    ctx.textAlign = 'left';

    // Accent line
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, 44, W, 2);

    // Metric cards (2 rows × 3 cols)
    const metrics = [
      { value: data.apm || 0, label: 'APM' },
      { value: data.maxCps || 0, label: 'PEAK CPS' },
      { value: data.hz || '---', label: 'POLLING (HZ)' },
      { value: data.maxSimultaneous || 0, label: 'MAX NKRO' },
      { value: data.totalPresses || 0, label: 'TOTAL PRESSES' },
      { value: data.latencyPeakMs || '---', label: 'LATENCY (MS)' }
    ];

    const cardW = 170;
    const cardH = 72;
    const gap = 14;
    const startX = (W - (3 * cardW + 2 * gap)) / 2;
    const startY = 60;

    metrics.forEach((m, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const x = startX + col * (cardW + gap);
      const y = startY + row * (cardH + gap);

      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      this._roundRect(ctx, x, y, cardW, cardH, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 26px "Segoe UI", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(String(m.value), x + cardW / 2, y + cardH / 2 - 2);

      ctx.fillStyle = DIM;
      ctx.font = '9px "Segoe UI", sans-serif';
      ctx.fillText(m.label, x + cardW / 2, y + cardH - 10);
      ctx.textAlign = 'left';
    });

    // === TOP KEYS HEATMAP ===
    const topKeysY = startY + 2 * (cardH + gap) + 16;
    ctx.fillStyle = GREEN;
    ctx.font = 'bold 11px "Segoe UI", sans-serif';
    ctx.fillText('TOP KEYS', 20, topKeysY);

    // Divider line
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fillRect(20, topKeysY + 8, W - 40, 1);

    const topKeys = (data.topKeys || []).slice(0, 5);
    const maxKeyCount = topKeys.length > 0 ? topKeys[0].count : 1;
    const barAreaX = 80;
    const barMaxW = W - barAreaX - 80;

    topKeys.forEach((k, i) => {
      const y = topKeysY + 20 + i * 24;
      const pct = k.count / maxKeyCount;
      const barW = Math.max(pct * barMaxW, 4);

      // Key name
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 11px monospace';
      ctx.fillText(k.key, 20, y + 8);

      // Bar
      const barGrad = ctx.createLinearGradient(barAreaX, 0, barAreaX + barW, 0);
      barGrad.addColorStop(0, 'rgba(0,255,157,0.6)');
      barGrad.addColorStop(1, 'rgba(0,255,157,0.15)');
      ctx.fillStyle = barGrad;
      this._roundRect(ctx, barAreaX, y, barW, 14, 3);
      ctx.fill();

      // Count
      ctx.fillStyle = GREEN;
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(String(k.count), W - 20, y + 10);
      ctx.textAlign = 'left';
    });

    // === DIAGNOSTICS ROW ===
    const diagY = topKeysY + 20 + Math.max(topKeys.length, 1) * 24 + 16;
    ctx.fillStyle = GREEN;
    ctx.font = 'bold 11px "Segoe UI", sans-serif';
    ctx.fillText('DIAGNOSTICS', 20, diagY);

    const diagItems = [
      { label: 'CHATTER', value: data.chatterCount || 0 },
      { label: 'STUCK', value: data.stuckCount || 0 },
      { label: 'CHORD SPLIT', value: data.chordSplitCount || 0 }
    ];
    const diagCardW = (W - 40 - 2 * 10) / 3;

    diagItems.forEach((d, i) => {
      const x = 20 + i * (diagCardW + 10);
      const y = diagY + 12;

      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      this._roundRect(ctx, x, y, diagCardW, 36, 6);
      ctx.fill();
      ctx.stroke();

      const isGood = d.value === 0;
      ctx.fillStyle = isGood ? GREEN : '#ff4444';
      ctx.font = 'bold 16px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(String(d.value), x + diagCardW / 2 - 20, y + 24);

      ctx.fillStyle = DIM;
      ctx.font = '9px "Segoe UI", sans-serif';
      ctx.fillText(d.label, x + diagCardW / 2 + 16, y + 24);
      ctx.textAlign = 'left';
    });

    // Verdict
    const issues = (data.chatterCount || 0) + (data.stuckCount || 0);
    let verdictLabel = 'EXCELLENT';
    let verdictColor = GREEN;
    if (issues > 0 && issues <= 3) { verdictLabel = 'GOOD'; verdictColor = '#f0c040'; }
    else if (issues > 3) { verdictLabel = 'NEEDS ATTENTION'; verdictColor = '#ff4444'; }

    // Footer
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.fillRect(0, H - 38, W, 38);
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, H - 38, W, 1);

    ctx.fillStyle = verdictColor;
    ctx.font = 'bold 12px "Segoe UI", sans-serif';
    ctx.fillText(`VERDICT: ${verdictLabel}`, 20, H - 16);

    ctx.fillStyle = DIM;
    ctx.font = '9px "Segoe UI", sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('tools.cytificlabs.com/kb-tester', W - 20, H - 16);
    ctx.textAlign = 'left';

    // Try Web Share API, else download
    try {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (navigator.share && blob) {
        const file = new File([blob], 'cytific-kb-results.png', { type: 'image/png' });
        await navigator.share({ title: 'My Keyboard Test Results', text: 'Check out my keyboard stats from Cytific KB Tester!', files: [file] });
        return;
      }
    } catch (e) { /* If share fails or is cancelled, fall through to download */ }

    // Fallback: download
    const imageURL = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = imageURL;
    link.download = `cytific-kb-share-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export const exportService = new ExportService();
